"use client";

import { getLangFromLocale } from "@/apiUtils/commonUtils";
import ArrowIcon from "@/assets/icons/arrowBottom.svg";
import { StoreTimings } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import Image from "next/image";
import { useParams } from "next/navigation";
import { FC, useEffect, useMemo, useState } from "react";

/**
 * Find the next opening time for a store
 */
const getNextOpeningTime = (
  days: Array<{ name: string; start: string; end: string; isClosed: boolean }>,
  currentDayOfWeek: number,
  lang: string = "en"
): string => {
  // Check today first (in case it opens later today)
  const today = days[currentDayOfWeek];
  if (!today.isClosed && today.start) {
    const dayAbbrev = lang === "en" ? today.name?.slice(0, 3) : today.name;
    return `${today.start}, ${dayAbbrev}`;
  }

  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const dayIndex = (currentDayOfWeek + i) % 7;
    const day = days[dayIndex];

    if (!day.isClosed && day.start) {
      const dayAbbrev = lang === "en" ? day.name?.slice(0, 3) : day.name;
      return `${day.start}, ${dayAbbrev}`;
    }
  }

  // All days closed
  return "";
};

interface IProps {
  open?: boolean;
  className?: string;
  timings: StoreTimings;
  isStoreTimings?: boolean;
  onChange?: (value: boolean) => void;
  translations?: Record<string, string>;
  statusNow?: string;
}

export const DisplayStoreTimings: FC<IProps> = ({
  open,
  timings,
  onChange,
  className,
  translations,
  isStoreTimings,
  statusNow,
}) => {
      // Early return if timings is null or undefined
  if (!timings) {
    return null;
  }
  const [isOpen, setIsOpen] = useState(open ?? false);
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);

  const { days, today, isOpenNow, currentDayOfWeek } = useMemo(() => {
    const now = new Date();
    const currentDayIndex = now.getDay();
    const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

    const getDayTimings = (dayName: string, startHour: string, endHour: string) => {
      const start = timings.setTimingFlag ? startHour : timings.weeklyStartHour;
      const end = timings.setTimingFlag ? endHour : timings.weeklyEndHour;
      const isClosed = !start || !end; // empty from mapper → closed

      return { name: dayName, start, end, isClosed };
    };

    const days = [
      getDayTimings(
        translations?.find_stores_monday_label || "Monday",
        timings.mondayStartHour,
        timings.mondayEndHour,
      ),
      getDayTimings(
        translations?.find_stores_tuesday_label || "Tuesday",
        timings.tuesdayStartHour,
        timings.tuesdayEndHour,
      ),
      getDayTimings(
        translations?.find_stores_wednesday_label || "Wednesday",
        timings.wednesdayStartHour,
        timings.wednesdayEndHour,
      ),
      getDayTimings(
        translations?.find_stores_thursday_label || "Thursday",
        timings.thursdayStartHour,
        timings.thursdayEndHour,
      ),
      getDayTimings(
        translations?.find_stores_friday_label || "Friday",
        timings.fridayStartHour,
        timings.fridayEndHour,
      ),
      getDayTimings(
        translations?.find_stores_saturday_label || "Saturday",
        timings.saturdayStartHour,
        timings.saturdayEndHour,
      ),
      getDayTimings(
        translations?.find_stores_sunday_label || "Sunday",
        timings.sundayStartHour,
        timings.sundayEndHour,
      ),
    ];

    const currentDayOfWeek = currentDayIndex === 0 ? 6 : currentDayIndex - 1;
    const today = days[currentDayOfWeek];
    const adjustedEnd = today.end === "00:00" ? "23:59" : today.end;
    // If start and end are both "00:00", store is closed
    const isOpenNow = today && !today.isClosed && !(today.start === "00:00" && today.end === "00:00") && currentTime >= today.start && currentTime <= adjustedEnd;

    return {
      days,
      today,
      isOpenNow,
      currentDayOfWeek,
    };
  }, [timings, translations]);
  
  // Extract nested ternary into a variable
  const buttonText = useMemo(() => {
    if (isStoreTimings) {
      // Use isOpenNow to determine if store is currently open or closed
      if (!isOpenNow) {
        // ✅ FIXED: Show "Opens at [time], [day]" when closed
        const opensAtLabel = translations?.find_store_opens_at_label || "Opens at";
        const nextOpeningTime = getNextOpeningTime(days, currentDayOfWeek, lang);
        return nextOpeningTime
          ? `${opensAtLabel} · ${nextOpeningTime}`
          : `${translations?.find_closed_label || "Closed"}`;
      }
      // Store is open - show "Closes at · [time], [day]"
      const closesAtLabel = translations?.find_store_closes_at_label || "Closes at";
      const dayAbbrev = lang === "en" ? today.name?.slice(0, 3) : today.name;
      return `${closesAtLabel} · ${today.end}, ${dayAbbrev}`;
    }
    
    // Optometrist hours logic
    const optometristLabel = translations?.find_store_optometrist_hours_label || "";
    if (!isOpenNow) {
      return `${optometristLabel} · ${translations?.find_closed_label || "Closed"}`;
    }
    return `${optometristLabel} · ${today.end}`;
  }, [isStoreTimings, isOpenNow, today, translations, lang, days, currentDayOfWeek]);
  const handleChange = () => {
    const value = !isOpen;
    setIsOpen(value);
    onChange?.(value);
  };

  useEffect(() => {
    if (open === undefined) return;
    setIsOpen(open);
  }, [open]);

  return (
    <div className={cn("w-fit", className)}>
      <button
        onClick={handleChange}
        className="flex w-fit items-center gap-3 bg-white transition-colors"
      >
        <Image
          src={ArrowIcon}
          alt="arrow down icon"
          className={`h-[0.438rem] w-[0.438rem] origin-center text-sm transition-transform duration-500 ${
            isOpen ? "rotate-180 transform" : ""
          }`}
          height={5}
          width={5}
        />
        <span className="text-sm font-normal">
            {buttonText}
          </span>
          {statusNow ? (
            <h6 className={`fw-bold ${statusNow === "Closed" ? "text-error2" : "text-success"}`}>
              {statusNow === "Closed" ? translations?.find_closed_label : translations?.find_store_open_now_label}
            </h6>
          ) : (
            isStoreTimings && (
              <h6 className={`fw-bold ${isOpenNow ? "text-success" : "text-error2"}`}>
                {isOpenNow ? translations?.find_store_open_now_label : translations?.find_closed_label}
              </h6>
            )
          )}
      </button>

      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white p-4 pl-5">
          <div className="">
            {days.map((item, index) => (
              <div
                key={index}
                className="flex w-[11.375rem] items-center justify-between gap-4 text-[0.875rem] leading-4"
              >
                <div className="flex w-[5.125rem] justify-start">
                  <span className="font-normal">{item.name}</span>
                </div>

                <div className="flex w-[5.25rem] justify-start">
                  <span className="text-gray-600">
                    {item.isClosed ? translations?.find_closed_label || "Closed" : `${item.start}-${item.end}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
